import React, { useRef, useState } from "react";
import axios from "axios";
import { Upload, Loader2, FileDown, Trash2, ShieldCheck, ShieldX, FileText } from "lucide-react";
import { API, inputCls, btnPrimary, Field, Empty, fmtDateTime, KYC_CATEGORIES, KYC_VERIFICATION } from "./shared";
import { compressImageIfNeeded } from "../../../utils/compressImage";

const DOC_LABELS = {
  aviso_operaciones: "Aviso de Operaciones", registro_publico: "Registro Público", cedula_pasaporte: "Cédula o Pasaporte",
  recibo_servicio: "Recibo de Agua o Luz", carta_bancaria: "Carta de Certificación Bancaria",
};

const download = async (fileId, filename, headers) => {
  const res = await axios.get(`${API}/onboarding/file/${fileId}`, { headers, responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = filename || fileId; document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

const ClientKyc = ({ appId, headers, canManage, originalDocs, kycDocs, onChange }) => {
  const inputRef = useRef(null);
  const [category, setCategory] = useState("cedula_pasaporte");
  const [comment, setComment] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);

  const upload = async (file) => {
    setUploading(true);
    setError("");
    try {
      const prepared = await compressImageIfNeeded(file);
      const data = new FormData();
      data.append("category", category);
      data.append("comment", comment);
      data.append("file", prepared);
      await axios.post(`${API}/crm/clients/${appId}/kyc`, data, { headers });
      setComment("");
      onChange();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "No se pudo subir el documento");
    } finally {
      setUploading(false);
    }
  };

  const setVerification = async (doc, verification) => {
    let comment = doc.comment || "";
    if (verification === "rechazado") {
      const reason = window.prompt("Motivo del rechazo (se enviará al comercio indicando qué debe volver a enviar):", comment);
      if (reason === null) return;
      comment = reason.trim();
    }
    setBusy(doc.id + verification);
    try {
      await axios.patch(`${API}/crm/kyc/${doc.id}`, { verification, comment }, { headers });
      onChange();
    } finally {
      setBusy(null);
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`¿Eliminar ${doc.filename}?`)) return;
    setBusy(doc.id + "del");
    try {
      await axios.delete(`${API}/crm/kyc/${doc.id}`, { headers });
      onChange();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="crm-kyc">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-black/45 mb-2">Documentos del registro</p>
        <div className="flex flex-wrap gap-2">
          {(originalDocs || []).map((d) => (
            <button key={d.file_id} data-testid={`kyc-original-${d.doc_type}`} disabled={!canManage} onClick={() => download(d.file_id, d.filename, headers)}
              className="inline-flex items-center gap-2 text-[11px] font-bold px-3 py-2 rounded-xl border border-black/10 bg-white hover:border-[#0fb013] hover:text-[#0fb013] disabled:opacity-50">
              <FileDown size={12} /> {DOC_LABELS[d.doc_type] || d.doc_type}
            </button>
          ))}
        </div>
      </div>

      {canManage && (
        <div className="bg-white border border-black/[0.06] rounded-2xl p-4 sm:p-5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-[#0fb013] mb-3">Subir documento KYC</p>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Categoría">
              <select data-testid="kyc-category" className={inputCls} value={category} onChange={(e) => setCategory(e.target.value)}>
                {Object.entries(KYC_CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="Comentario (opcional)"><input data-testid="kyc-comment" className={inputCls} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Ej: vigente hasta 2027" /></Field>
            <div className="flex items-end">
              <input ref={inputRef} data-testid="kyc-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
              <button data-testid="kyc-upload-btn" className={`${btnPrimary} w-full`} disabled={uploading} onClick={() => inputRef.current?.click()}>
                {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Seleccionar y subir
              </button>
            </div>
          </div>
          <p className="mt-2 text-[11px] font-medium text-black/40">PDF, JPG, PNG · máx. 1.5MB (las imágenes se comprimen automáticamente)</p>
          {error && <p className="mt-2 text-[12px] font-bold text-[#ff603e]">{error}</p>}
        </div>
      )}

      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-black/45 mb-2">Documentos KYC adicionales ({kycDocs.length})</p>
        {kycDocs.length === 0 ? <Empty icon={FileText} text="Aún no hay documentos KYC adicionales" /> : (
          <div className="space-y-2">
            {kycDocs.map((d) => {
              const v = KYC_VERIFICATION[d.verification] || KYC_VERIFICATION.pendiente;
              return (
                <div key={d.id} data-testid={`kyc-doc-${d.id}`} className="bg-white border border-black/[0.06] rounded-2xl px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[13px] font-bold text-black">{KYC_CATEGORIES[d.category] || d.category}</span>
                      <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full ${v.cls}`}>{v.label}</span>
                    </div>
                    <p className="text-[11px] font-medium text-black/50 truncate">{d.filename} · {(d.size / 1024).toFixed(0)} KB · {d.uploaded_by?.name} · {fmtDateTime(d.created_at)}{d.comment ? ` · ${d.comment}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button title="Descargar" data-testid={`kyc-download-${d.id}`} className="p-2 rounded-lg hover:bg-black/[0.06]" onClick={() => download(d.file_id, d.filename, headers)}><FileDown size={14} /></button>
                    {canManage && (
                      <>
                        <button title="Marcar verificado" data-testid={`kyc-verify-${d.id}`} className="p-2 rounded-lg hover:bg-[#0fe814]/20 text-[#0fb013]" disabled={d.verification === "verificado" || busy === d.id + "verificado"} onClick={() => setVerification(d, "verificado")}><ShieldCheck size={14} /></button>
                        <button title="Marcar rechazado y avisar al comercio por correo" data-testid={`kyc-reject-${d.id}`} className="p-2 rounded-lg hover:bg-[#ff603e]/10 text-[#ff603e]" disabled={d.verification === "rechazado" || busy === d.id + "rechazado"} onClick={() => setVerification(d, "rechazado")}><ShieldX size={14} /></button>
                        <button title="Eliminar" data-testid={`kyc-delete-${d.id}`} className="p-2 rounded-lg hover:bg-black/[0.06] text-black/50" disabled={busy === d.id + "del"} onClick={() => remove(d)}>{busy === d.id + "del" ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}</button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientKyc;
